import { useEffect, useMemo, useRef, useState } from 'react';
import type { AltYParticipant, AltYUser } from '../types/global';
import type { PinnedAgent } from '../lib/pinnedAgents';
import { AUTO_AGENT } from '../lib/pinnedAgents';
import { listAltYUsers } from '../lib/altYApi';

type ParticipantsPopoverProps = {
  participants: AltYParticipant[];
  pinnedAgents: PinnedAgent[];
  onAddParticipant: (params: {
    type: 'user' | 'agent';
    id: string;
    label?: string;
    agentId?: string;
    mode?: string;
  }) => Promise<any>;
  onClose: () => void;
};

type Selection = { type: 'user' | 'agent'; id: string };

export function ParticipantsPopover({
  participants,
  pinnedAgents,
  onAddParticipant,
  onClose,
}: ParticipantsPopoverProps) {
  const [allUsers, setAllUsers] = useState<AltYUser[]>([]);
  const [query, setQuery] = useState('');
  const [picked, setPicked] = useState<Selection[]>([]);
  const [inviting, setInviting] = useState(false);
  const [error, setError] = useState('');
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    void listAltYUsers().then((list) => setAllUsers(list || []));
  }, []);

  useEffect(() => {
    window.setTimeout(() => searchRef.current?.focus(), 0);
  }, []);

  // Already-added participants — shown checked + disabled so the user sees
  // who's in the chat without those slots eating the search results.
  const existingKeys = useMemo(() => {
    const set = new Set<string>();
    for (const p of participants) {
      const id = p.type === 'user' ? p.id.trim().toLowerCase() : (p.agentId || p.id);
      set.add(`${p.type}:${id}`);
    }
    return set;
  }, [participants]);

  // AUTO is not a real participant — it's only a routing target on the
  // composer picker, so exclude it from the share roster.
  const agents = useMemo(
    () => pinnedAgents.filter((a) => a.id !== AUTO_AGENT.id),
    [pinnedAgents],
  );

  type Row =
    | { kind: 'header'; key: string; label: string }
    | { kind: 'agent'; key: string; agent: PinnedAgent }
    | { kind: 'user'; key: string; user: AltYUser };

  const rows = useMemo<Row[]>(() => {
    const q = query.trim().toLowerCase();
    const matchedAgents = agents.filter((a) => {
      if (!q) return true;
      const label = (a.label || '').toLowerCase();
      const title = (a.flow?.title || '').toLowerCase();
      return label.includes(q) || title.includes(q);
    });
    const matchedUsers = allUsers.filter((u) => {
      if (!q) return true;
      const label = (u.label || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      return label.includes(q) || email.includes(q);
    });
    const out: Row[] = [];
    if (matchedAgents.length) {
      out.push({ kind: 'header', key: 'h-agents', label: 'Agents' });
      for (const a of matchedAgents) out.push({ kind: 'agent', key: `a-${a.id}`, agent: a });
    }
    if (matchedUsers.length) {
      out.push({ kind: 'header', key: 'h-users', label: 'Users' });
      for (const u of matchedUsers) out.push({ kind: 'user', key: `u-${u.id}`, user: u });
    }
    return out;
  }, [agents, allUsers, query]);

  useEffect(() => {
    function handleOutsideClick(event: MouseEvent) {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, [onClose]);

  function togglePick(sel: Selection) {
    setPicked((prev) => {
      const idx = prev.findIndex((p) => p.type === sel.type && p.id === sel.id);
      if (idx >= 0) {
        const copy = prev.slice();
        copy.splice(idx, 1);
        return copy;
      }
      return [...prev, sel];
    });
  }

  function isChecked(sel: Selection): boolean {
    return picked.some((p) => p.type === sel.type && p.id === sel.id);
  }

  async function handleInvite() {
    if (!picked.length) return;
    setInviting(true);
    setError('');
    try {
      for (const sel of picked) {
        if (sel.type === 'agent') {
          const agent = agents.find((a) => a.id === sel.id);
          if (!agent) continue;
          await onAddParticipant({
            type: 'agent',
            id: agent.id,
            label: agent.label,
            agentId: agent.id,
            mode: agent.mode,
          });
        } else {
          const user = allUsers.find((u) => u.id === sel.id);
          if (!user) continue;
          await onAddParticipant({ type: 'user', id: user.id, label: user.label });
        }
      }
      setPicked([]);
    } catch (err) {
      setError((err as Error).message || 'Failed to add participant');
    } finally {
      setInviting(false);
    }
  }

  return (
    <section
      ref={popoverRef}
      className="alty-chat-info-popover alty-participants-popover"
      role="dialog"
      aria-label="Manage Participants"
      style={{ right: '40px', top: '56px', width: '380px' }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <header className="alty-chat-info-head">
        <div className="alty-chat-info-kicker">
          <i className="fas fa-users" aria-hidden="true" />
          Chat Participants
        </div>
        <button
          type="button"
          className="alty-icon-button alty-chat-info-close"
          title="Close"
          aria-label="Close participants popover"
          onClick={onClose}
        >
          <i className="fas fa-times" aria-hidden="true" />
        </button>
      </header>

      <div className="alty-participants-content" style={{ maxHeight: '480px', overflowY: 'auto', padding: '12px' }}>
        {error ? (
          <div className="alty-chat-title-error" style={{ marginBottom: '8px' }}>
            {error}
          </div>
        ) : null}

        <div className="alty-chat-info-label" style={{ marginBottom: '6px' }}>
          Active in Chat ({participants.length})
        </div>

        <ul className="alty-participants-list" style={{ listStyle: 'none', padding: 0, margin: '0 0 12px 0' }}>
          {participants.map((p) => {
            const isAgent = p.type === 'agent';
            return (
              <li
                key={`${p.type}-${p.id}`}
                className="alty-participant-row"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '6px 8px',
                  borderRadius: '6px',
                  marginBottom: '4px',
                  background: 'rgba(255, 255, 255, 0.03)',
                }}
              >
                <i
                  className={`fas ${isAgent ? 'fa-robot' : 'fa-user'}`}
                  style={{
                    marginRight: '8px',
                    color: isAgent ? '#a855f7' : '#3b82f6',
                    width: '16px',
                    textAlign: 'center',
                  }}
                  aria-hidden="true"
                />
                <span className="alty-participant-name" style={{ flexGrow: 1, fontSize: '13px', fontWeight: 500 }}>
                  {p.label}
                </span>
                {p.owner ? (
                  <span
                    className="alty-pinned-pill"
                    style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(59, 130, 246, 0.15)', color: '#3b82f6', borderRadius: '4px' }}
                  >
                    Owner
                  </span>
                ) : isAgent ? (
                  <span
                    className="alty-pinned-pill"
                    style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(168, 85, 247, 0.15)', color: '#a855f7', borderRadius: '4px' }}
                  >
                    AI Agent
                  </span>
                ) : (
                  <span
                    className="alty-pinned-pill"
                    style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '4px' }}
                  >
                    User
                  </span>
                )}
              </li>
            );
          })}
        </ul>

        <div className="alty-scribe-divider" style={{ margin: '12px 0' }} />

        <div className="alty-chat-info-label" style={{ marginBottom: '6px' }}>
          Invite Agents or Users
        </div>

        <div className="alty-agent-picker-menu" style={{ position: 'relative', boxShadow: 'none', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '8px', maxHeight: 'unset' }}>
          <input
            ref={searchRef}
            type="text"
            className="alty-agent-picker-input"
            placeholder="Search agents or users…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            disabled={inviting}
          />
          <ul
            className="alty-agent-picker-list"
            role="listbox"
            aria-multiselectable="true"
            style={{ maxHeight: '240px' }}
          >
            {rows.length === 0 ? (
              <li className="alty-agent-picker-empty">No matching agents or users</li>
            ) : rows.map((row) => {
              if (row.kind === 'header') {
                return (
                  <li key={row.key} className="alty-agent-picker-section" aria-hidden="true">
                    {row.label}
                  </li>
                );
              }
              if (row.kind === 'agent') {
                const sel: Selection = { type: 'agent', id: row.agent.id };
                const already = existingKeys.has(`agent:${row.agent.id}`);
                const checked = already || isChecked(sel);
                return (
                  <li
                    key={row.key}
                    role="option"
                    aria-selected={checked}
                    aria-disabled={already}
                    className={
                      'alty-agent-picker-option'
                      + (checked ? ' selected' : '')
                      + (already ? ' disabled' : '')
                    }
                    onMouseDown={(e) => {
                      e.preventDefault();
                      if (!already && !inviting) togglePick(sel);
                    }}
                  >
                    <span
                      className={'alty-picker-check' + (checked ? ' checked' : '')}
                      aria-hidden="true"
                    >
                      {checked ? <i className="fas fa-check" aria-hidden="true" /> : null}
                    </span>
                    <i className={`fas ${row.agent.icon || 'fa-robot'}`} aria-hidden="true" />
                    <span className="alty-agent-picker-option-label">{row.agent.label}</span>
                    {already ? (
                      <span className="alty-pinned-pill" style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(255,255,255,0.06)' }}>
                        In chat
                      </span>
                    ) : null}
                  </li>
                );
              }
              const sel: Selection = { type: 'user', id: row.user.id };
              const userKey = row.user.id.trim().toLowerCase();
              const already = existingKeys.has(`user:${userKey}`);
              const checked = already || isChecked(sel);
              return (
                <li
                  key={row.key}
                  role="option"
                  aria-selected={checked}
                  aria-disabled={already}
                  className={
                    'alty-agent-picker-option'
                    + (checked ? ' selected' : '')
                    + (already ? ' disabled' : '')
                  }
                  onMouseDown={(e) => {
                    e.preventDefault();
                    if (!already && !inviting) togglePick(sel);
                  }}
                >
                  <span
                    className={'alty-picker-check' + (checked ? ' checked' : '')}
                    aria-hidden="true"
                  >
                    {checked ? <i className="fas fa-check" aria-hidden="true" /> : null}
                  </span>
                  <i className="fas fa-user" aria-hidden="true" />
                  <span className="alty-agent-picker-option-label">
                    {row.user.label || row.user.email}
                  </span>
                  {already ? (
                    <span className="alty-pinned-pill" style={{ fontSize: '10px', padding: '1px 6px', background: 'rgba(255,255,255,0.06)' }}>
                      In chat
                    </span>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '10px' }}>
          <button
            type="button"
            className="alty-send-button"
            disabled={inviting || !picked.length}
            style={{ padding: '6px 14px', fontSize: '12px' }}
            onClick={handleInvite}
            title={picked.length ? `Invite ${picked.length} selected` : 'Pick at least one'}
          >
            <i className="fas fa-plus" aria-hidden="true" style={{ marginRight: '6px' }} />
            Invite{picked.length ? ` (${picked.length})` : ''}
          </button>
        </div>
      </div>
    </section>
  );
}
